/*
  The discipline filter of the bar (DEC-1103-03): "All", or one discipline. It narrows nothing by itself; the views read it and
  fade or show what belongs to another discipline. The choice is in the address (`?d=<code>`), so it survives a reload and a
  shared link, and a small store remembers it while the person moves between screens (a tab link has no `?d=` of its own).

  The address is the one source for readers: `useDisciplineFilter().discipline` is read from it, so a screen rendered on its own
  sees exactly the address it was given. The store only puts the choice back on an address that lost it.
*/

import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { create } from "zustand";

/** The name of the address parameter. */
export const DISCIPLINE_PARAM = "d";

/** The discipline code named by an address's query string, or null for "All" (no code, or an empty one). */
export function disciplineFromSearch(search: string): string | null {
  const code = new URLSearchParams(search).get(DISCIPLINE_PARAM)?.trim();
  return code ? code : null;
}

/** The query string with the discipline set (or taken away for null), every other parameter kept. */
export function withDiscipline(search: string, code: string | null): string {
  const params = new URLSearchParams(search);
  if (code) params.set(DISCIPLINE_PARAM, code);
  else params.delete(DISCIPLINE_PARAM);
  const text = params.toString();
  return text ? `?${text}` : "";
}

interface Remembered {
  /** The code the person chose last, null for "All". */
  chosen: string | null;
  remember: (code: string | null) => void;
}

export const useDisciplineStore = create<Remembered>((set) => ({
  chosen: null,
  remember: (chosen) => set({ chosen }),
}));

export interface DisciplineFilter {
  /** The discipline code to show, or null for All. */
  discipline: string | null;
  /** Choose a discipline, or null for All. The address changes in place, with no new history entry. */
  choose: (code: string | null) => void;
}

/** The filter for a screen: what it is now, and the way to change it. */
export function useDisciplineFilter(): DisciplineFilter {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search, hash } = location;
  const discipline = disciplineFromSearch(search);
  const choose = useCallback(
    (code: string | null) => {
      // The store first: the address change that follows must find the choice already made, or it would be put back.
      useDisciplineStore.getState().remember(code);
      void navigate({ pathname, search: withDiscipline(search, code), hash }, { replace: true });
    },
    [navigate, pathname, search, hash],
  );
  return { discipline, choose };
}

/**
 * Keeps the choice across screens. It starts from the address the app was opened on (so one person's choice never leaks into
 * the next sign-in). After that, an address with a discipline is taken as the choice (back and forward work), and an address
 * without one gets the remembered choice put back, in place. Used once, by the shell.
 */
export function useDisciplineFilterSync(): void {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    const fromAddress = disciplineFromSearch(search);
    const store = useDisciplineStore.getState();
    if (!started.current) {
      started.current = true;
      store.remember(fromAddress);
      return;
    }
    if (fromAddress !== null) {
      if (store.chosen !== fromAddress) store.remember(fromAddress);
      return;
    }
    if (store.chosen !== null) void navigate({ pathname, search: withDiscipline(search, store.chosen), hash }, { replace: true });
  }, [pathname, search, hash, navigate]);
}
